import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { authApi } from '../../../api';

const OtpPage = ({ error: initialError = '', message: initialMessage = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState(initialError);
  const [message, setMessage] = useState(initialMessage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailFromState = location?.state?.email || '';
  const RESET_TOKEN_KEY = 'resetToken';
  const RESET_TOKEN_EXP_KEY = 'resetTokenExp';
  const RESET_EMAIL_KEY = 'resetEmail';

  const logoSrc = `${process.env.PUBLIC_URL || ''}/assets/images/chat_logo.png`;

  useEffect(() => {
    if (location?.state?.message) {
      setMessage(location.state.message);
    } else if (!initialMessage) {
      setMessage('We\'ve sent an OTP to your registered Email Address. Please check your email and enter the code below.');
    }
  }, [initialMessage, location]);

  useEffect(() => {
    if (!emailFromState) {
      setError('Missing email. Please restart the reset process.');
    }
  }, [emailFromState]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!emailFromState) return;
    setIsSubmitting(true);
    try {
      const response = await authApi.verifyOtp({ email: emailFromState, otp });
      const token = response?.data?.resetToken || '';
      const expiryMinutes = response?.data?.expiresInMinutes || 10;
      if (token) {
        const expiresAt = Date.now() + expiryMinutes * 60 * 1000;
        sessionStorage.setItem(RESET_TOKEN_KEY, token);
        sessionStorage.setItem(RESET_TOKEN_EXP_KEY, String(expiresAt));
        sessionStorage.setItem(RESET_EMAIL_KEY, emailFromState);
      }
      navigate('/reset-password', { state: { email: emailFromState, token } });
    } catch (err) {
      const errMsg = err?.response?.data?.message || 'Invalid OTP. Please try again.';
      setError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        backgroundImage: `linear-gradient(135deg, rgba(37, 211, 102, 0.65) 0%, rgba(26, 160, 80, 0.7) 50%, rgba(20, 126, 63, 0.75) 100%), url(${process.env.PUBLIC_URL || ''}/assets/images/whatsapp_bg1.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 480,
          borderRadius: 4,
          boxShadow: 10,
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(6px)',
          border: '1px solid rgba(255,255,255,0.4)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={2} alignItems="center">
            <Box sx={{ alignSelf: 'flex-start' }}>
              <Button
                variant="text"
                startIcon={<ArrowLeft size={18} />}
                href="/forgot-password"
                sx={{ px: 0, minWidth: 0 }}
              >
                Back
              </Button>
            </Box>
            <Box component="img" src={logoSrc} alt="ChattyBO" sx={{ height: 40 }} />
            <Typography variant="h6">Verify Your OTP</Typography>

            {error && error.length > 0 && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            {message && message.length > 0 && (
              <Alert severity="info" sx={{ width: '100%' }}>
                {message}
              </Alert>
            )}
          </Stack>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Stack spacing={2}>
              <TextField
                label="OTP"
                name="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                fullWidth
                required
                inputProps={{ maxLength: 6 }}
              />

              <Stack direction="row" justifyContent="flex-end">
                <Button type="submit" variant="contained" disabled={!emailFromState || isSubmitting}>
                  {isSubmitting ? 'Verifying...' : 'Verify'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OtpPage;
